import { useEffect } from "react";

interface SEOProps {
  title: string;
  description: string;
  canonical: string;
  ogImage?: string;
  noindex?: boolean;
  ogType?: "website" | "article" | "product";
}

const DEFAULT_OG_IMAGE =
  "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/6e36ca09-b621-468c-b52b-ff005874f4f9/id-preview-6b43e9e5--298d3c4b-7b8b-490d-bf60-18b4d9daa802.lovable.app-1774007688920.png";

/**
 * Dynamically sets title, meta description, canonical, OG tags, hreflang, and robots for sub-pages.
 */
export const useSEO = ({ title, description, canonical, ogImage, noindex, ogType }: SEOProps) => {
  useEffect(() => {
    document.title = title;

    const setMeta = (attr: string, key: string, content: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    const setLink = (rel: string, href: string, extraAttrs?: Record<string, string>) => {
      const selector = extraAttrs
        ? `link[rel="${rel}"][${Object.entries(extraAttrs).map(([k, v]) => `${k}="${v}"`).join("][")}]`
        : `link[rel="${rel}"]`;
      let el = document.querySelector(selector) as HTMLLinkElement | null;
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        if (extraAttrs) {
          Object.entries(extraAttrs).forEach(([k, v]) => el?.setAttribute(k, v));
        }
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    // Meta description
    setMeta("name", "description", description);

    // Robots
    setMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");

    // Canonical
    setLink("canonical", canonical);

    // Hreflang (single entry — avoids duplicate alternate URLs)
    setLink("alternate", canonical, { hreflang: "en-in" });


    // Open Graph
    const img = ogImage || DEFAULT_OG_IMAGE;
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", canonical);
    setMeta("property", "og:image", img);
    setMeta("property", "og:type", ogType || "website");
    setMeta("property", "og:site_name", "Taher Steel Tube Co.");
    setMeta("property", "og:locale", "en_IN");

    // Twitter
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", img);
    setMeta("name", "twitter:card", "summary_large_image");
  }, [title, description, canonical, ogImage, noindex, ogType]);
};
